import { useEffect } from 'react'

/**
 * The capture's scroll-reveal, in React.
 *
 * One element uses it — `#hundred`, the animated "100x" wordmark on
 * `/marketing-automation`. Its stylesheet is a reveal keyed off a class that
 * nothing was left to add:
 *
 *   #hundred            { opacity: 0 }
 *   #hundred.in-view    { opacity: 1; ...20s dash animation }
 *
 * The `.in-view` class came from a `<script>` inside the page's
 * `w-embed w-script` block. The porter kept every embed's `<style>` and
 * dropped every embed's `<script>`, so the rule survived and the thing that
 * satisfied it did not — leaving the graphic at `opacity: 0` permanently. It
 * has been invisible on that page for the life of the port, and because it is
 * a ported page nothing in the repo checks it.
 *
 * Found while auditing the sibling embed on `/roi-calculator`, which lost its
 * script the same way.
 *
 * Driven from here rather than by editing the page, for the same reason as
 * `useWebflowTabs`: the ported markup stays exactly as captured and keeps its
 * own classes; this only supplies the behaviour that used to sit beside it.
 *
 * `IntersectionObserver` rather than a scroll listener — the original polled
 * on scroll, which is the one part worth not reproducing.
 */
export function useInViewReveal(route: string) {
  useEffect(() => {
    const targets = [...document.querySelectorAll<HTMLElement>('#hundred')]
    if (!targets.length) return

    /*
     * Reduced motion still means visible.
     *
     * The alternative — leaving the class off — would honour the preference by
     * hiding the content outright, which is not what anybody is asking for
     * when they ask for less animation. The class goes on immediately; the
     * page's own stylesheet drops the 20s dash under the same query.
     */
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (still || typeof IntersectionObserver === 'undefined') {
      for (const t of targets) t.classList.add('in-view')
      return
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue
          e.target.classList.add('in-view')
          // One-shot: the animation is `forwards` and re-firing it on every
          // scroll back would restart a 20s draw the reader already watched.
          io.unobserve(e.target)
        }
      },
      { threshold: 0.2 },
    )
    for (const t of targets) io.observe(t)
    return () => io.disconnect()
  }, [route])
}
